import React from 'react';
import validator from 'validator';
import classnames from 'classnames';
import { withGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { UserContext } from '../../context';
import {
  FETCH_GENERATED_USERNAME,
  GET_EMAIL_FROM_HASH,
  SET_NEW_USER_PASSWORD_URL,
  TEMPTED_ACCOUNTS_URL,
} from '../../constants/apiUrls/apiUrls';
import callApi from '../../utils/callApi';
import Alert from '../../components/generalComponents/Alert';
import { doLogin } from '../../utils/auth';
import { isGuestOrFreemiumUser } from '../../utils/getUserDetails';
import TransparentLoader from '../../components/Loaders/TransparentLoader';

import styles from './css/NewUserAccountCreation.module.css';
import './css/ManageAccount.css';
import { AccountTabs } from './AccountTabs';
import PasswordChangeSection from './PasswordChangeSection';
import AuthenticationStore from '../../store/AuthenticationStore';
import { observer } from 'mobx-react';

@observer
class ManageAccountPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAlertVisible: false,
      password: '',
      confirmPassword: '',
      email: '',
      content: '',
      nickname: '',
      passwordErrorVisible: false,
      passwordError: '',
      nicknameUpdateCounter: 0,
      isLoading: false,
    };

    const urlParams = new URLSearchParams(window.location.search);
    this.emailVerificationCode = urlParams.get('code');
  }

  componentDidMount() {
    const { user, isNewUserPasswordSetupPage } = this.props;
    if (user.user && user.user.account) {
      this.setState({
        email: user.user.account.email,
        nickname: user.user.account.nickname,
        nicknameUpdateCounter: user.user.account.nicknameUpdateCounter || 0,
        /* Made this change because the default value for nicknameUpdateCounter is undefined from Backend. */
      });
    }
    if (isNewUserPasswordSetupPage) {
      callApi(GET_EMAIL_FROM_HASH, 'GET', { hash: encodeURIComponent(this.emailVerificationCode) }, {}).then((res) => {
        if (res.status === 200) {
          this.setState({ email: res.data.email });
        }
      }).catch((err) => {
        window.location.href = '/home';
      });
    }
  }


  handleAutoLogin = () => {
    const { email, password } = this.state;
    const { rememberMe } = AuthenticationStore
    const trimmedEmail = email && email.trim();
    const trimmedPassword = password && password.trim();

    this.requestRecaptcha().
      then((recaptcha) => {
        const data = `username=${trimmedEmail}&password=${encodeURIComponent(trimmedPassword)}&rememberMe=${rememberMe}&recaptcha=${recaptcha}`;

        doLogin(data).
          then(() => {
            this.setState({ isLoading: false });
            window.location.href = '/home';
          }).
          catch(() => {
            console.error('Login error');
          });
      });
  };

  requestRecaptcha = () => {
    const { googleReCaptchaProps: { executeRecaptcha } } = this.props;

    return executeRecaptcha('login');
  };

  showAlert = (content) => {
    this.setState({
      isAlertVisible: true,
      content,
    });
  };

  hideAlert = () => {
    this.setState({
      isAlertVisible: false,
      content: '',
    });
  };

  handleChange = (name) => (e) => {
    this.setState({
      [name]: e.target.value,
      isAlertVisible: false,
    });
  };

  newPasswordValid = () => {
    const { password, confirmPassword } = this.state;
    if (!password) {
      return false;
    }
    const username = sessionStorage.getItem('username');
    const trimmedPassword = password.trim();
    let error = '';
    if (!validator.isLength(trimmedPassword, { min: 8 })) {
      error = 'Password needs to be at least 8 characters long and shouldn\'t contain spaces';
    } else if (password !== confirmPassword) {
      error = 'Passwords do not match';
    } else if (trimmedPassword === username) {
      error = 'Password must be different than your username';
    } else if (trimmedPassword.substring(0, 9) === 'nakednews' || trimmedPassword.substring(0, 8) === 'password') {
      error = 'Password shouldn\'t start with \'nakednews\' or \'password\'';
    }

    if (error) {
      this.setState({
        passwordErrorVisible: true,
        passwordError: error,
      });

      return false;
    }

    return true;
  };

  handleSubmit = (e) => {
    // Added nicknameUpdateCounter and user prop here
    const { user, isNewUserPasswordSetupPage } = this.props;
    const { email, password, confirmPassword, nickname, nicknameUpdateCounter } = this.state;
    const data = {};
    data.username = sessionStorage.getItem('username');
    e.preventDefault();
    if (email) {
      data.email = email.trim();
    }
    if (nickname) {
      data.nickname = nickname;
    }
    const trimmedPassword = password && password.trim();
    const trimmedConfirmPassword = confirmPassword && confirmPassword.trim();
    if (trimmedPassword && !this.newPasswordValid()) {
      return;
    }
    if (trimmedPassword) {
      if (trimmedPassword === trimmedConfirmPassword) {
        data.password = trimmedPassword;
      } else {
        this.setState({
          isAlertVisible: true,
          content: 'Passwords must match',
        });

        return;
      }
    }

    if (isNewUserPasswordSetupPage) {
      this.setState({ isLoading: true });
      const newUserPasswordData = {
        emailHash: encodeURIComponent(this.emailVerificationCode),
        password: trimmedPassword,
      };
      callApi(SET_NEW_USER_PASSWORD_URL, 'POST', {}, newUserPasswordData).then((res) => {
        if (res.status === 201) {
          this.handleAutoLogin();
        }
      }).catch((err) => {
        this.setState({
          isAlertVisible: true,
          content: 'There was an error setting your password. Please try again',
        });
      });

      return;
    }

    // Function to handle Nickname change event | Only make calls to tempted if nickname changes and if the user is not an anchor
    const handleNicknameChanges = (nicknameUpdateCounter) => {
      const [defaultNickname, domainName] = email.split('@');
      if (nicknameUpdateCounter === 0 && nickname !== defaultNickname && !user.user.account.anchor) {
        callApi(TEMPTED_ACCOUNTS_URL, 'PUT', false, data, true, true, true, true);
      }
    };

    callApi(FETCH_GENERATED_USERNAME, 'PATCH', false, data, true, true).
      then(handleNicknameChanges(nicknameUpdateCounter)). // Calls nicknameChange handler function
      then((res) => {
        if (res.status === 200) {
          this.setState({
            isAlertVisible: true,
            content: 'Settings are saved',
          });
          window.location.reload();
        }
      }).
      catch((err) => console.log(err));
  };

  render() {
    const {
      password,
      confirmPassword,
      email,
      nickname,
      isAlertVisible,
      isLoading,
      content,
      passwordErrorVisible,
      passwordError,
      nicknameUpdateCounter,
    } = this.state;
    const { user, isNewUserPasswordSetupPage } = this.props;
    const isAnchor = !!user?.user?.account?.anchor;
    const isWhitelisted = user.user ? user.features.includes('ANCHOR_PORTAL') : false;
    const isCam4User = user?.user?.account?.site === 'CAM4';

    return (
      <div
        className={classnames(
          'flex-1', {
            [styles.newUserPasswordSetupPage]: isNewUserPasswordSetupPage,
            'content': !isNewUserPasswordSetupPage,
            [styles.freemiumUserContent]: isGuestOrFreemiumUser(user),
          }
        )}
      >
        {isLoading && <TransparentLoader />}
        {isAlertVisible && (
          <Alert onClose={this.hideAlert}>{content}</Alert>
        )}
        <div
          className={
            classnames('container', { [styles.container]: isNewUserPasswordSetupPage })
          }
        >
          <div className={styles.accountInfoWrapper}>
            {!isNewUserPasswordSetupPage && <AccountTabs active="/account" />}
            <h1>Your Naked News Account</h1>
            <form
              acceptCharset="UTF-8"
              className="formtastic form-horizontal customer"
              id="edit_customer_156374"
              noValidate="novalidate"
              onSubmit={this.handleSubmit}
            >
              <fieldset className={styles.fieldSet}>
                <legend className={styles.legend}>
                  <span>Account Information</span>
                </legend>
                <PasswordChangeSection
                  confirmPassword={confirmPassword}
                  email={email}
                  handleChange={this.handleChange}
                  isCam4User={isCam4User}
                  password={password}
                  passwordError={passwordError}
                  passwordErrorVisible={passwordErrorVisible}
                />
                <div
                  className="string input optional stringish form-group email-field"
                  id="customer_email_input"
                >
                  <label
                    className="control-label"
                    htmlFor="customer_email"
                  >
                    Email Address
                  </label>
                  <div>
                    <input
                      className="form-control"
                      disabled={true}
                      id="customer_email"
                      name="customer[email]"
                      placeholder="Email"
                      type="text"
                      value={email}
                      onChange={this.handleChange('email')}
                    />
                  </div>
                </div>
                {isWhitelisted && !isAnchor &&
                  <div
                    className="string input optional stringish form-group"
                    id="customer_nickname_input"
                  >
                    <label
                      className="control-label"
                      htmlFor="customer_nickname"
                    >
                      Nickname (maximum 12 characters)
                    </label>
                    <div>
                      <input
                        className="form-control"
                        disabled={nicknameUpdateCounter > 0}
                        id="customer_nickname"
                        maxLength={12}
                        name="customer[nickname]"
                        placeholder="Set a nickname"
                        type="text"
                        value={nickname}
                        onChange={this.handleChange('nickname')}
                      />
                    </div>
                  </div>
                }
              </fieldset>
              <div className="form-group">
                <div>
                  <button
                    className={styles.btnCurvedRed}
                    id="customer_submit_action"
                    name="button"
                    type="submit"
                  >
                    Update
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }
}

ManageAccountPage.contextType = UserContext;

export default withGoogleReCaptcha(ManageAccountPage);
